import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { resetPassword } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import Form from "../components/Form";
import Button from "../components/Button";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { addToast } = useToasts();

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      addToast("Passwords do not match", "error-warning");
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword(token, password, passwordConfirm);
      loginWithToken(response.data.token, response.data.user);
      navigate("/");
    } catch (err) {
      const errorMsg =
        err.response?.data?.details?.[0] || "Failed to reset password";

      // If email not verified, redirect to login with message
      if (errorMsg.includes("verify")) {
        addToast("Password reset successfully", "success-info");
        addToast("Please verify your email and try logging in", "success-info");
        // Try to extract email from JWT token payload for convenience
        let userEmail = "";
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          userEmail = payload.email || "";
        } catch {
          // If can't extract email, user will need to enter it
        }
        navigate("/login", {
          state: {
            fromPasswordReset: true,
            email: userEmail,
          },
        });
        return;
      } else {
        // Show toast for other errors (invalid token, expired, etc.)
        addToast(errorMsg, "error-warning");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Form
        title="Invalid Link"
        showCancel={false}
        showSubmit={false}
        footerLinks={
          <Button
            className="button-btn button-btn-blue"
            onClick={() => navigate("/forgot-password")}
          >
            Request New Link
          </Button>
        }
      >
        <p>The reset link is invalid or has expired.</p>
      </Form>
    );
  }

  return (
    <Form
      title="Reset Password"
      onSubmit={handleSubmit}
      error={null}
      loading={loading}
      submitText="Reset Password"
      showCancel={false}
      footerLinks={<Link to="/login">Back to Login</Link>}
    >
      <label htmlFor="password">
        New Password
        <div style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={loading}
            style={{ paddingRight: password ? "60px" : "10px" }}
          />
          {password && (
            <>
              <button
                type="button"
                onClick={() => setPassword("")}
                style={{
                  position: "absolute",
                  right: "38px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#666",
                  padding: "0",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#666",
                  padding: "0",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </>
          )}
        </div>
      </label>
      <label htmlFor="passwordConfirm">
        Confirm New Password
        <div style={{ position: "relative" }}>
          <input
            type={showPasswordConfirm ? "text" : "password"}
            id="passwordConfirm"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            minLength={8}
            disabled={loading}
            style={{ paddingRight: passwordConfirm ? "60px" : "10px" }}
          />
          {passwordConfirm && (
            <>
              <button
                type="button"
                onClick={() => setPasswordConfirm("")}
                style={{
                  position: "absolute",
                  right: "38px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#666",
                  padding: "0",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#666",
                  padding: "0",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {showPasswordConfirm ? "🙈" : "👁️"}
              </button>
            </>
          )}
        </div>
      </label>
    </Form>
  );
}

export default ResetPassword;
